import axios from 'axios'
import {formatApiError} from '../../../shared/utils/apiErrorMessage'

import type {PermissionCatalogEntry} from './permissionGrouping'

const API_URL = process.env.REACT_APP_API_URL
const ADMIN_ACCESS_API_URL = `${API_URL}/admin/access`

export type AccessScope = 'ALL' | 'COMPANY' | 'VESSEL'
export type AccessGrantEffect = 'ALLOW' | 'DENY'
export type AccessResolutionMode = 'LEGACY_ONLY' | 'COMPARE_ONLY' | 'ENFORCE_NEW_ADMIN_ONLY'
export type AccessDecisionSourceTag =
  | 'ROLE_BASELINE'
  | 'TENANT_ROLE_OVERRIDE'
  | 'CREW_RANK'
  | 'BUNDLE'
  | 'USER_OVERRIDE_ALLOW'
  | 'USER_OVERRIDE_DENY'
  | 'SCOPE_CLAMPED'

export type AdminAccessBundlePermission = {
  id?: number
  permissionCode: string
  defaultScope?: AccessScope | null
  effect: AccessGrantEffect
}

export type AdminAccessBundle = {
  id: number
  code: string
  name: string
  moduleHint?: string | null
  active: boolean
  systemDefined: boolean
  permissions: AdminAccessBundlePermission[]
}

export type AdminUserBundleAssignment = {
  id: number
  bundleId: number
  bundleCode: string
  bundleName: string
  active: boolean
  assignedByUserId?: number | null
  assignedAt?: string | null
}

export type AdminUserAccessOverride = {
  id?: number
  permissionCode: string
  effect: AccessGrantEffect
  scope?: AccessScope | null
  reason?: string | null
  active?: boolean | null
  invalidatedReason?: string | null
  invalidatedAt?: string | null
  invalidatedByUserId?: number | null
  updatedByUserId?: number | null
  updatedByUsername?: string | null
  updatedAt?: string | null
}

/** Default packages by account role (and optional crew rank). */
export type AdminAccessBundleDefaultRule = {
  id?: number
  roleType: string
  rankId?: number | null
  rankLabel?: string | null
  bundleId?: number | null
  bundleCode?: string | null
  bundleName?: string | null
  sortOrder?: number | null
  active?: boolean | null
}

export type CrewRankPermissionMapping = {
  rankId: number
  rankLabel?: string | null
  bundleId?: number | null
  bundleCode?: string | null
  inherited: boolean
  permissionCodes: string[]
}

export type RankDropdownOption = {
  id: number
  rank: string
}

export const getCrewRankPermissionMappings = async (): Promise<CrewRankPermissionMapping[]> => {
  const res = await axios.get<CrewRankPermissionMapping[]>(
    `${ADMIN_ACCESS_API_URL}/crew-rank-permissions`
  )
  return res.data || []
}

export const updateCrewRankPermissionMapping = async (
  rankId: number,
  permissionCodes: string[]
): Promise<CrewRankPermissionMapping> => {
  const res = await axios.put<CrewRankPermissionMapping>(
    `${ADMIN_ACCESS_API_URL}/crew-rank-permissions`,
    {
      rankId,
      permissionCodes,
    }
  )
  return res.data
}

export const resetCrewRankPermissionMapping = async (rankId: number): Promise<void> => {
  await axios.delete(`${ADMIN_ACCESS_API_URL}/crew-rank-permissions/${rankId}`)
}

export type AdminUserScopeBinding = {
  id?: number
  userId: number
  hardMaxScope?: AccessScope
  companyId?: number | null
  vesselId?: number | null
  source?: string | null
}

export type EffectivePermissionDecision = {
  allowed: boolean
  scope?: AccessScope | null
  sourceTag?: AccessDecisionSourceTag | null
  sourceTrail?: AccessDecisionSourceTag[]
}

export type EffectiveAccessGraph = {
  userId: number
  roleId?: number | null
  roleName?: string | null
  hardMaxScope?: AccessScope | null
  resolutionMode?: AccessResolutionMode
  defaultBundleIds?: number[]
  assignedBundleIds?: number[]
  permissions: Record<string, EffectivePermissionDecision>
}

export type EffectiveAccessComparisonResult = {
  mode: AccessResolutionMode
  effectiveGraph: EffectiveAccessGraph
  legacyPermissions?: string[]
  newPermissions?: string[]
  onlyInLegacy?: string[]
  onlyInNew?: string[]
}

export type AuthChangeAudit = {
  id: number
  actorUserId?: number | null
  targetUserId?: number | null
  changeType: string
  beforeJson?: string | null
  afterJson?: string | null
  createdAt: string
}

const getErrorMessage = (error: any, fallback: string): string => formatApiError(error, fallback)

type CachedArrayRequest<T> = {
  promise: Promise<T[]> | null
  value: T[] | null
}

const createCachedArrayRequest = <T>(): CachedArrayRequest<T> => ({
  promise: null,
  value: null,
})

const permissionRegistryCache = createCachedArrayRequest<string>()
const permissionCatalogCache = createCachedArrayRequest<PermissionCatalogEntry>()
const adminAccessBundlesCache = createCachedArrayRequest<AdminAccessBundle>()
const adminBundleDefaultRulesCache = createCachedArrayRequest<AdminAccessBundleDefaultRule>()

const cloneArray = <T>(items: T[]): T[] => items.slice()

const readCachedArray = async <T>(
  cache: CachedArrayRequest<T>,
  loader: () => Promise<T[]>
): Promise<T[]> => {
  if (cache.value) {
    return cloneArray(cache.value)
  }
  if (!cache.promise) {
    cache.promise = loader()
      .then((items) => {
        cache.value = cloneArray(items || [])
        return cache.value
      })
      .catch((error) => {
        cache.promise = null
        throw error
      })
  }
  const items = await cache.promise
  return cloneArray(items || [])
}

const resetCachedArray = <T>(cache: CachedArrayRequest<T>) => {
  cache.promise = null
  cache.value = null
}

const invalidateAdminAccessBundleCache = () => {
  resetCachedArray(adminAccessBundlesCache)
}

const invalidateAdminBundleDefaultRuleCache = () => {
  resetCachedArray(adminBundleDefaultRulesCache)
}

export const getAdminPermissionRegistry = async (): Promise<string[]> => {
  try {
    return await readCachedArray(permissionRegistryCache, async () => {
      const res = await axios.get<string[]>(`${ADMIN_ACCESS_API_URL}/permissions`)
      return res.data || []
    })
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to load permission registry'))
  }
}

export type {PermissionCatalogEntry}

export const getAdminPermissionCatalog = async (): Promise<PermissionCatalogEntry[]> => {
  try {
    return await readCachedArray(permissionCatalogCache, async () => {
      const res = await axios.get<PermissionCatalogEntry[]>(
        `${ADMIN_ACCESS_API_URL}/permissions/catalog`
      )
      return res.data || []
    })
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to load permission catalog'))
  }
}

export const getAdminAccessBundles = async (): Promise<AdminAccessBundle[]> => {
  try {
    return await readCachedArray(adminAccessBundlesCache, async () => {
      const res = await axios.get<AdminAccessBundle[]>(`${ADMIN_ACCESS_API_URL}/bundles`)
      return res.data || []
    })
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to load bundles'))
  }
}

export const createAdminAccessBundle = async (payload: {
  code: string
  name: string
  moduleHint?: string | null
  active?: boolean
}): Promise<AdminAccessBundle> => {
  try {
    const res = await axios.post<AdminAccessBundle>(`${ADMIN_ACCESS_API_URL}/bundles`, payload)
    invalidateAdminAccessBundleCache()
    return res.data
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to create bundle'))
  }
}

export const updateAdminAccessBundle = async (
  bundleId: number,
  payload: {
    code: string
    name: string
    moduleHint?: string | null
    active?: boolean
  }
): Promise<AdminAccessBundle> => {
  try {
    const res = await axios.put<AdminAccessBundle>(
      `${ADMIN_ACCESS_API_URL}/bundles/${bundleId}`,
      payload
    )
    invalidateAdminAccessBundleCache()
    return res.data
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to update bundle'))
  }
}

export const updateAdminBundlePermissions = async (
  bundleId: number,
  permissions: AdminAccessBundlePermission[]
): Promise<AdminAccessBundlePermission[]> => {
  try {
    const res = await axios.put<AdminAccessBundlePermission[]>(
      `${ADMIN_ACCESS_API_URL}/bundles/${bundleId}/permissions`,
      {permissions}
    )
    invalidateAdminAccessBundleCache()
    return res.data || []
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to update bundle permissions'))
  }
}

export const getAdminUserBundles = async (userId: number): Promise<AdminUserBundleAssignment[]> => {
  try {
    const res = await axios.get<AdminUserBundleAssignment[]>(
      `${ADMIN_ACCESS_API_URL}/users/${userId}/bundles`
    )
    return res.data || []
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to load user bundles'))
  }
}

export const updateAdminUserBundles = async (
  userId: number,
  bundleIds: number[]
): Promise<AdminUserBundleAssignment[]> => {
  try {
    const res = await axios.put<AdminUserBundleAssignment[]>(
      `${ADMIN_ACCESS_API_URL}/users/${userId}/bundles`,
      {bundleIds}
    )
    return res.data || []
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to update user bundles'))
  }
}

export const getAdminUserOverrides = async (userId: number): Promise<AdminUserAccessOverride[]> => {
  try {
    const res = await axios.get<AdminUserAccessOverride[]>(
      `${ADMIN_ACCESS_API_URL}/users/${userId}/overrides`
    )
    return res.data || []
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to load user overrides'))
  }
}

export const updateAdminUserOverrides = async (
  userId: number,
  overrides: AdminUserAccessOverride[]
): Promise<AdminUserAccessOverride[]> => {
  try {
    const res = await axios.put<AdminUserAccessOverride[]>(
      `${ADMIN_ACCESS_API_URL}/users/${userId}/overrides`,
      {overrides}
    )
    return res.data || []
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to update user overrides'))
  }
}

const RANKS_API_URL = `${API_URL}/ranks`

export const getRankDropdownOptions = async (): Promise<RankDropdownOption[]> => {
  try {
    const res = await axios.get<RankDropdownOption[]>(`${RANKS_API_URL}/drop-down`)
    return res.data || []
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to load ranks'))
  }
}

export const getAdminBundleDefaultRules = async (): Promise<AdminAccessBundleDefaultRule[]> => {
  try {
    return await readCachedArray(adminBundleDefaultRulesCache, async () => {
      const res = await axios.get<AdminAccessBundleDefaultRule[]>(
        `${ADMIN_ACCESS_API_URL}/bundle-default-rules`
      )
      return res.data || []
    })
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to load default package rules'))
  }
}

export const replaceAdminBundleDefaultRules = async (
  rules: AdminAccessBundleDefaultRule[]
): Promise<AdminAccessBundleDefaultRule[]> => {
  try {
    const res = await axios.put<AdminAccessBundleDefaultRule[]>(
      `${ADMIN_ACCESS_API_URL}/bundle-default-rules`,
      {
        rules,
      }
    )
    invalidateAdminBundleDefaultRuleCache()
    return res.data || []
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to save default package rules'))
  }
}

export const getAdminUserScope = async (userId: number): Promise<AdminUserScopeBinding> => {
  try {
    const res = await axios.get<AdminUserScopeBinding>(
      `${ADMIN_ACCESS_API_URL}/users/${userId}/scope`
    )
    return res.data
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to load user scope'))
  }
}

export const updateAdminUserScope = async (
  userId: number,
  payload: {
    hardMaxScope: AccessScope
    companyId?: number | null
    vesselId?: number | null
    source?: string | null
  }
): Promise<AdminUserScopeBinding> => {
  try {
    const res = await axios.put<AdminUserScopeBinding>(
      `${ADMIN_ACCESS_API_URL}/users/${userId}/scope`,
      payload
    )
    return res.data
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to update user scope'))
  }
}

export type AdminUserPermissionMatrixRow = {
  permissionCode: string
  permissionId?: number | null
  displayName?: string | null
  description?: string | null
  moduleCategory?: string | null
  menuLabel?: string | null
  subMenuLabel?: string | null
  inheritedFromRole: boolean
  parentAllowed: boolean
  parentSource?: string | null
  userAllowOverride: boolean
  userDenyOverride: boolean
  userOverrideActive: boolean
  userOverrideInvalidated: boolean
  crewRankManaged: boolean
  overrideInvalidatedReason?: string | null
  overrideUpdatedAt?: string | null
  overrideUpdatedByUserId?: number | null
  overrideUpdatedByUsername?: string | null
  effectiveAllowed: boolean
  effectiveSource?: string | null
}

export const getAdminUserPermissionMatrix = async (
  userId: number
): Promise<AdminUserPermissionMatrixRow[]> => {
  try {
    const res = await axios.get<AdminUserPermissionMatrixRow[]>(
      `${ADMIN_ACCESS_API_URL}/users/${userId}/permission-matrix`
    )
    return res.data || []
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to load permission matrix'))
  }
}

export const getAdminEffectivePreview = async (
  userId: number
): Promise<EffectiveAccessComparisonResult> => {
  try {
    const res = await axios.get<EffectiveAccessComparisonResult>(
      `${ADMIN_ACCESS_API_URL}/users/${userId}/effective-preview`
    )
    return res.data
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to load effective preview'))
  }
}

export const getAdminAccessHistory = async (
  userId: number,
  limit = 100
): Promise<AuthChangeAudit[]> => {
  try {
    const res = await axios.get<AuthChangeAudit[]>(
      `${ADMIN_ACCESS_API_URL}/users/${userId}/history`,
      {params: {limit}}
    )
    return res.data || []
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to load access history'))
  }
}

export type AdminUserOverrideReconciliationRow = {
  permissionCode: string
  effect: AccessGrantEffect
  storedActive: boolean
  parentAllowed: boolean
  parentSource?: string | null
  action: string
  detail?: string | null
}

export type AdminUserOverrideReconciliationResult = {
  userId: number
  roleType?: string | null
  crewUser: boolean
  crewRankManaged: boolean
  rankLabel?: string | null
  dryRun: boolean
  scannedOverrides: number
  invalidatedCount: number
  reactivatedCount: number
  unchangedCount: number
  rows: AdminUserOverrideReconciliationRow[]
}

export const getAdminUserOverrideReconciliationDryRun = async (
  userId: number
): Promise<AdminUserOverrideReconciliationResult> => {
  try {
    const res = await axios.get<AdminUserOverrideReconciliationResult>(
      `${ADMIN_ACCESS_API_URL}/users/${userId}/override-reconciliation/dry-run`
    )
    return res.data
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to load override reconciliation dry run'))
  }
}

export const reconcileAdminUserOverrides = async (
  userId: number
): Promise<AdminUserOverrideReconciliationResult> => {
  try {
    const res = await axios.post<AdminUserOverrideReconciliationResult>(
      `${ADMIN_ACCESS_API_URL}/users/${userId}/override-reconciliation`
    )
    return res.data
  } catch (error: any) {
    throw new Error(getErrorMessage(error, 'Failed to reconcile user overrides'))
  }
}
